import {
  createProject,
  deleteProject,
  editProject,
  listOfProject,
  viewProject,
} from "Api/Project";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import BaseModal from "Components/Base/BaseModal";
import TableContainer from "Components/Base/BaseTable";
import DeleteModal from "Components/Base/DeleteModal";
import withRouter from "Components/Common/withRouter";
import {
  checkStatusCodeSuccess,
  Loading,
  notFound,
  Search,
} from "Components/constants/Common";
import {
  addProjectLabel,
  editProjectsLabel,
  locationKey,
  locationLabel,
  projectKey,
  projectLabel,
  projectName,
  projectsLabel,
} from "Components/constants/Project";
import {
  ActionKey,
  ActionLabel,
  cancelLabel,
  DeleteLabel,
  editLabel,
  NewLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import {
  InputPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { useFormik } from "formik";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { RiSearchLine } from "react-icons/ri";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import * as Yup from "yup";

const Project = (props: any) => {
  document.title = projectsLabel;

  const [loader, setLoader] = useState<boolean>(false);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    null
  );
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [modalLoader, setModalLoader] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [projectSearchData, setProjectSearchData] = useState<Array<any>>([]);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");

  const projectModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    formik.resetForm();
    setSelectedProjectId(null);
    setEditMode(false);
  };

  const fetchProjects = async () => {
    const payload = {
      pageSize,
      page: currentPage,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfProject(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setProjects(res?.data?.projects || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setProjects([]);
        }
      })
      .catch(() => {
        setProjects([]);
      })
      .finally(() => setLoader(false));
  };

  const fetchSearchProjectData = () => {
    const payload = {
      pageSize,
      page: currentPage,
      search: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfProject(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setProjectSearchData(res?.data?.projects || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setProjectSearchData([]);
        }
      })
      .catch(() => {
        setProjectSearchData([]);
      })
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    if (searchValue) {
      fetchSearchProjectData();
    } else {
      fetchProjects();
    }
  }, [currentPage, pageSize, searchValue, sortOrder, columnName]);

  const formik = useFormik({
    initialValues: {
      project_name: "",
      project_location: "",
    },
    validationSchema: Yup.object({
      project_name: Yup.string().required(
        validationMessages.required(projectName)
      ),
      project_location: Yup.string().required(
        validationMessages.required(locationLabel)
      ),
    }),
    onSubmit: async (values) => {
      setSubmitLoader(true);
      const payload = {
        project_name: values.project_name,
        project_location: values.project_location,
      };

      if (selectedProjectId) {
        editProject(selectedProjectId, payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              fetchProjects();
              closeModal();
            } else {
              toast.error(res?.message);
            }
          })
          .catch((error) => {
            toast.error(error?.response?.data?.message || error?.message);
          })
          .finally(() => setSubmitLoader(false));
      } else {
        createProject(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              fetchProjects();
              closeModal();
            } else {
              toast.error(res?.message);
            }
          })
          .catch((error) => {
            toast.error(error?.response?.data?.message || error?.message);
            setSubmitLoader(false);
          })
          .finally(() => setSubmitLoader(false));
      }
    },
  });

  const openDeleteModal = (id: number) => {
    setSelectedProjectId(id);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setSelectedProjectId(null);
  };

  const handleDeleteProject = () => {
    if (selectedProjectId !== null) {
      setBtnLoader(true);
      deleteProject(selectedProjectId)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            fetchProjects();
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message || error?.message);
        })
        .finally(() => {
          setBtnLoader(false);
          closeDeleteModal();
        });
    }
  };

  const viewProjectData = (id: number) => {
    projectModal();
    setModalLoader(true);
    viewProject(id)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          formik.setValues({
            project_name: res?.data?.project_name,
            project_location: res?.data?.project_location,
          });
          setSelectedProjectId(id);
        } else {
          toast.error(res?.message);
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || error?.message);
      })
      .finally(() => setModalLoader(false));
  };

  const openEditModal = async (id: number) => {
    setEditMode(true);
    viewProjectData(id);
    setModalLoader(true);
  };

  const handleSearchValueChange = (value: any) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  const handleFetchData = (page: any) => {
    setCurrentPage(page);
  };

  const handleFetchSorting = (
    page: any,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const projectColumns = useMemo(
    () => [
      {
        header: projectLabel,
        accessorKey: projectKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: locationLabel,
        accessorKey: locationKey,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ActionLabel,
        accessorKey: ActionKey,
        cell: (cell: any) => {
          return (
            <div className="d-flex justify-content-start enable-action flex-wrap gap-2 icon">
              <span className="tooltip-container">
                <i
                  className={`${""} mdi mdi-pencil-outline fs-4 text-info cursor-pointer`}
                  onClick={() => openEditModal(cell?.row?.original?.id)}
                ></i>
                <span className="tooltip-text">{editLabel}</span>
              </span>

              <span className="tooltip-container">
                <i
                  className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"
                  onClick={() => openDeleteModal(cell?.row?.original?.id)}
                ></i>
                <span className="tooltip-text">{DeleteLabel}</span>
              </span>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{projectsLabel} </span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={projectModal}
              children={
                <>
                  <i className="mdi mdi-plus" /> {NewLabel}
                </>
              }
            />
          </div>
        </div>

        <div className="card py-2 px-4 overflow-x-auto">
          {loader || projects.length > 0 ? (
            <TableContainer
              customPageSize={pageSize}
              setCustomPageSize={setPageSize}
              isGlobalFilter={true}
              columns={projectColumns}
              data={searchValue ? projectSearchData : projects || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              onSearch={handleSearchValueChange}
              totalNumberOfRows={totalNumberOfRows}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>

        <BaseModal
          open={isModalOpen}
          toggle={closeModal}
          title={editMode ? editProjectsLabel : addProjectLabel}
          submit={formik.handleSubmit}
          closeLabel={cancelLabel}
          submitLabel={editMode ? updateLabel : submitLabel}
          closeLabelColor="danger"
          submitLabelColor="success"
          loader={submitLoader}
        >
          {modalLoader ? (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner>{Loading}</Spinner>
            </div>
          ) : (
            <div className="form-group">
              <BaseInput
                type="text"
                label={projectName}
                placeholder={InputPlaceHolder(projectName)}
                name="project_name"
                className="form-control"
                value={formik.values.project_name}
                onChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                touched={formik.touched.project_name}
                error={formik.errors.project_name}
                required
              />

              <div className="mt-2">
                <BaseInput
                  type="textarea"
                  label={locationLabel}
                  placeholder={InputPlaceHolder(locationLabel)}
                  name="project_location"
                  className="form-control"
                  value={formik.values.project_location}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.project_location}
                  error={formik.errors.project_location}
                  required
                />
              </div>
            </div>
          )}
        </BaseModal>

        <DeleteModal
          show={deleteModal}
          onCloseClick={closeDeleteModal}
          onDeleteClick={handleDeleteProject}
          btnLoader={btnLoader}
        />
      </div>
    </div>
  );
};

export default withRouter(Project);
